import { Preferences } from "@capacitor/preferences";
import { KeychainAccess, SecureStorage } from "@aparajita/capacitor-secure-storage";
import { Capacitor } from "@capacitor/core";
import { APP_TARGET } from "../config";
import type { TokenResponse } from "./types";

const SESSION_KEY = `private-menu:${APP_TARGET}:session`;
const IS_NATIVE = Capacitor.isNativePlatform();
const storageReady = IS_NATIVE
  ? Promise.all([
      SecureStorage.setKeyPrefix("private-menu_"),
      SecureStorage.setDefaultKeychainAccess(KeychainAccess.whenUnlockedThisDeviceOnly),
    ])
  : Promise.resolve([]);

export async function readSession(): Promise<TokenResponse | null> {
  if (!IS_NATIVE) {
    const { value } = await Preferences.get({ key: SESSION_KEY });
    if (!value) return null;
    try {
      return JSON.parse(value) as TokenResponse;
    } catch {
      await Preferences.remove({ key: SESSION_KEY });
      return null;
    }
  }
  try {
    await storageReady;
  } catch {
    await Preferences.remove({ key: SESSION_KEY });
    return null;
  }
  let value: Awaited<ReturnType<typeof SecureStorage.get>> = null;
  try {
    value = await SecureStorage.get(SESSION_KEY, false);
  } catch {
    await SecureStorage.remove(SESSION_KEY).catch(() => false);
  }
  if (typeof value !== "string") {
    const legacy = await Preferences.get({ key: SESSION_KEY });
    value = legacy.value;
    if (value) {
      await SecureStorage.set(SESSION_KEY, value, false);
      await Preferences.remove({ key: SESSION_KEY });
    }
  }
  if (!value) return null;
  try {
    return JSON.parse(value) as TokenResponse;
  } catch {
    await clearSession();
    return null;
  }
}

export async function writeSession(session: TokenResponse): Promise<void> {
  if (!IS_NATIVE) {
    await Preferences.set({ key: SESSION_KEY, value: JSON.stringify(session) });
    return;
  }
  await storageReady;
  await SecureStorage.set(SESSION_KEY, JSON.stringify(session), false);
  await Preferences.remove({ key: SESSION_KEY });
}

export async function clearSession(): Promise<void> {
  if (!IS_NATIVE) {
    await Preferences.remove({ key: SESSION_KEY });
    return;
  }
  await storageReady.catch(() => undefined);
  await Promise.all([
    SecureStorage.remove(SESSION_KEY),
    Preferences.remove({ key: SESSION_KEY }),
  ]);
}
